import React, { useRef, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ContextMenu } from 'primereact/contextmenu'
import { useTranslation } from 'react-i18next'
import 'src/components/utils/utils.css'
import { LoadingContext, M_CheckBox, M_InputTextField } from 'src/M_Components'
import { Settings } from '@mui/icons-material'
import { Tooltip } from '@mui/material'

export function ShipmentChargesShipmentTable({ value, selectedShipments, setSelectedShipments }) {
    const ctx = useContext(LoadingContext)
    const { t } = useTranslation()
    const cm = useRef(null)

    const toggleShipment = (shipmentId) => {
        setSelectedShipments(prev =>
            prev.includes(shipmentId)
                ? prev.filter(s => s !== shipmentId)
                : [...prev, shipmentId]
        )
    }

    const columns = [
        { label: 'IMO', type: 'checkbox', code: 'IMO' },
        { label: 'Equipment Type', type: 'combobox' },
        { label: 'Cargo Description', type: 'textfield' },
        { label: 'Load Weight (KG)', type: 'textfield' },
        { label: 'Loading Date', type: 'date' },
        { label: 'SOC', type: 'checkbox', code: 'SOC' },
        { label: 'Reefer', type: 'checkbox', code: 'Reefer' },
        { label: 'Open Top/FLAT', type: 'checkbox', code: 'OpenTop' },
        { label: 'RORO', type: 'checkbox', code: 'RORO' },
    ]

    const checkboxValue = (rowData, code) => {
        if (rowData[code] === null) {
            return <span style={{ display: 'flex', backgroundColor: 'lightgrey', alignItems: 'center', justifyContent: 'space-around', height: '42px' }} />
        }
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', minHeight: '60px' }}>
                <M_CheckBox
                    checked={!!rowData[code]}
                    disabled={true}
                    color='var(--color-blue)'
                />
            </div>
        )
    }

    const textInput = (rowData, code) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', minHeight: '60px' }}>
            <M_InputTextField
                size='auto'
                margin='10px'
                padding='0px'
                paddingLeft='5px'
                disabled={true}
                type='text'
                value={rowData[code] ?? ''}
                disabledColor='var(--color-white)'
            />
        </div>
    )

    const textInputNumbers = (rowData, code) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', minHeight: '60px' }}>
            <M_InputTextField
                margin='10px'
                padding='0px'
                paddingLeft='5px'
                disabled={true}
                value={rowData[code]?.toString() ?? ''}
                type='number'
                disabledColor='var(--color-white)'
            />
        </div>
    )

    const textInputDate = (rowData, code) => {
        if (rowData[code] === null) {
            return <span style={{ display: 'flex', backgroundColor: 'lightgrey', alignItems: 'center', justifyContent: 'space-around', height: '42px' }} />
        }
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', minHeight: '60px' }}>
                <M_InputTextField
                    margin='10px'
                    padding='0px'
                    paddingLeft='5px'
                    disabled={true}
                    type='date'
                    value={rowData[code] ?? ''}
                    disabledColor='var(--color-white)'
                />
            </div>
        )
    }

    const equipmentPlusButton = (rowData, code) => {
        if (rowData[code] === null) {
            return <span style={{ display: 'flex', backgroundColor: 'lightgrey', alignItems: 'center', justifyContent: 'space-around', height: '42px' }} />
        }
        return (
            <span style={{
                borderTopLeftRadius: '5px',
                borderBottomLeftRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                height: '100%',
                minHeight: '60px'
            }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', flexDirection: 'column' }}>
                    <b>
                        {rowData.Number && rowData.CheckDigit ? `${rowData.Prefix} ${rowData.Number} ${rowData.CheckDigit}` : ''}
                    </b>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                        <div style={{ fontSize: '.7em', position: 'relative', left: '-10px' }}>
                            {rowData.EquipmentTransportKey}
                        </div>
                        {rowData[code]}
                    </div>
                </span>
            </span>
        )
    }

    const dynamicColumns = columns.map((col, key) => {
        if (col.type === 'checkbox') {
            const field = col.code === 'SOC' || col.code === 'Reefer' || col.code === 'OpenTop' || col.code === 'RORO' ? 'EquipmentIndex' : 'Shipment'
            return (
                <Column
                    key={key}
                    headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor }}
                    style={{ cursor: 'pointer', paddingRight: '0px' }}
                    field={field}
                    header={col.label}
                    body={(e) => checkboxValue(e, col.code)}
                />
            )
        }
        if (col.label === 'Load Weight (KG)') {
            return <Column key={key} headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor }} style={{ cursor: 'text' }} field='EquipmentIndex' header={col.label} body={(data) => textInputNumbers(data, col.label)} />
        }
        if (col.label === 'Cargo Description') {
            return <Column key={key} headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor }} style={{ cursor: 'text' }} field='EquipmentIndex' header={col.label} body={(data) => textInput(data, col.label)} />
        }
        if (col.label === 'Loading Date') {
            return <Column key={key} headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor }} style={{ cursor: 'text' }} field='EquipmentIndex' header={col.label} body={(data) => textInputDate(data, col.label)} />
        }
        if (col.label === 'Equipment Type') {
            return <Column key={key} headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor, minWidth: '150px' }} style={{ cursor: 'inherit' }} field='EquipmentIndex' header={col.label} body={(data) => equipmentPlusButton(data, col.label)} />
        }
        return <Column key={key} headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor }} style={{ cursor: 'inherit' }} field={col.label} header={col.label} />
    })

    const buttonEquipment = (data) => (
        <div style={{ minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title={t('equipmentOptions')}>
                <Settings style={{ color: 'var(--color-green)', transform: 'scale(.8)', cursor: 'default' }} />
            </Tooltip>
        </div>
    )

    const selectionBody = (data) => {
        const isSelected = selectedShipments.includes(data.ShipmentId)
        return (
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60px', cursor: 'pointer' }}
                onClick={() => toggleShipment(data.ShipmentId)}
            >
                <div style={{
                    width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
                    border: `1px solid ${isSelected ? ctx.primaryColor || 'var(--color-blue)' : '#aaa'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {isSelected && (
                        <div style={{
                            width: '9px', height: '9px', borderRadius: '50%',
                            backgroundColor: ctx.primaryColor || 'var(--color-blue)',
                        }} />
                    )}
                </div>
            </div>
        )
    }

    const subHeader = (data) => (
        <div
            className='cursorDefault heightSubHeader'
            style={{ paddingLeft: '5px', minWidth: '100px', height: 'auto !important', minHeight: '60px' }}
        >
            <div className='image-text'>{t('Shipment')} {data.Shipment}</div>
            <Tooltip title={t('shipmentOptions')}>
                <Settings className='cursorPointer' style={{ color: 'var(--color-green)', transform: 'scale(.8)' }} />
            </Tooltip>
        </div>
    )

    return (
        <div className='divPrincipalTable'>
            <div className='divTable' style={{ paddingBottom: '40px' }}>
                <ContextMenu model={[]} ref={cm} />
                <DataTable
                    value={value}
                    groupRowsBy={['Shipment', 'EquipmentIndex']}
                    rowGroupMode='rowspan'
                    className='tableEquipments'
                    size='small'
                >
                    <Column
                        field='Shipment'
                        headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor, width: '36px' }}
                        style={{ width: '36px' }}
                        header=''
                        body={selectionBody}
                    />
                    <Column
                        className='shipmentNameBooking'
                        headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor, minWidth: '100px' }}
                        field='Shipment'
                        header='Shipment'
                        body={subHeader}
                    />
                    {dynamicColumns}
                    <Column
                        field='EquipmentIndex'
                        headerStyle={{ borderBottom: `2px solid ${ctx.primaryColor}`, color: ctx.secondaryColor }}
                        body={buttonEquipment}
                    />
                </DataTable>
            </div>
        </div>
    )
}
